import { defineStore } from 'pinia'
import { ref } from 'vue'
import apiInstance from '@/utils/api'

export interface Partner {
  id: number
  company_name: string
  first_name: string
  last_name: string
}

export interface CompanyDetail {
  id: number
  company_name: string
  first_name: string
  last_name: string
  email: string
  street: string
  city: string
  zip_code: number
  country_id: number
  currency_code: string
  vat_number?: string | null
  partner: Partner | null
}

export interface CompaniesResponse {
  error: boolean
  message: string
  data: CompanyDetail[]
}

export interface CreateCompanyPayload {
  company_name: string
  first_name: string
  last_name: string
  partner_id?: number
  email: string
  password?: string
  street: string
  city: string
  zip_code: string
  country_id: number
  vat_number?: string | null
}

export interface ValidationError {
  [key: string]: string[]
}

export const useCompaniesListStore = defineStore('companiesList', () => {
  const companies = ref<CompanyDetail[]>([])
  const isLoading = ref(false)
  const error = ref('')
  const successMessage = ref('')

  const fetchCompanies = async () => {
    isLoading.value = true
    error.value = ''

    try {
      const response = await apiInstance.get('/companies')
      const data = response.data

      if (response.status !== 200 || (data && data.error === true)) {
        error.value = data?.message || 'Failed to fetch companies'
        isLoading.value = false
        return
      }

      if (data?.data && Array.isArray(data.data)) {
        companies.value = data.data
      }

    } catch (err: any) {
      console.error('Error fetching companies:', err)
      error.value = err.response?.data?.message || 'Network error while fetching companies'
    } finally {
      isLoading.value = false
    }
  }

  const createCompany = async (payload: CreateCompanyPayload) => {
    try {
      // Remove partner_id if not selected
      const payloadToSend = { ...payload }
      if (!payloadToSend.partner_id) {
        delete payloadToSend.partner_id
      }

      const response = await apiInstance.post('/companies', payloadToSend)
      const data = response.data

      if (response.status < 200 || response.status >= 300 || (data && data.error === true)) {
        // Handle validation errors from server (422 status)
        if (response.status === 422 && data?.errors) {
          const error: any = new Error(data?.message || 'Validation error')
          error.validationErrors = data.errors as ValidationError
          throw error
        }
        console.error('API error:', data?.message)
        throw new Error(data?.message || 'Failed to create company')
      }

      return {
        success: true,
        message: (data && data.message) || 'Company created successfully',
        data: data?.data,
      }
    } catch (err: any) {
      console.error('Error creating company:', err)

      // Handle axios error responses (including 422 validation errors)
      if (err.response) {
        const status = err.response.status
        const data = err.response.data

        // Handle validation errors from server (422 status)
        if (status === 422 && data?.errors) {
          const error: any = new Error(data?.message || 'Validation error')
          error.validationErrors = data.errors as ValidationError
          throw error
        }

        // Handle other API errors
        throw new Error(data?.message || err.message || 'Network error')
      }

      // Re-throw if already has validation errors attached
      if (err.validationErrors) {
        throw err
      }

      throw err
    }
  }

  const getCompanyById = async (companyId: number): Promise<CompanyDetail> => {
    try {
      const response = await apiInstance.get(`/companies/${companyId}`)
      const data = response.data

      if (response.status !== 200 || (data && data.error === true)) {
        throw new Error(data?.message || 'Failed to fetch company details')
      }

      return data.data
    } catch (err: any) {
      console.error('Error fetching company:', err)
      throw new Error(err.response?.data?.message || err.message || 'Network error')
    }
  }

  const editCompany = async (companyId: number, payload: CreateCompanyPayload) => {
    try {
      // Remove partner_id if not selected
      const payloadToSend = { ...payload }
      if (!payloadToSend.partner_id) {
        delete payloadToSend.partner_id
      }

      const response = await apiInstance.put(`/companies/${companyId}`, payloadToSend)
      const data = response.data

      if (response.status < 200 || response.status >= 300 || (data && data.error === true)) {
        // Handle validation errors from server (422 status)
        if (response.status === 422 && data?.errors) {
          const error: any = new Error(data?.message || 'Validation error')
          error.validationErrors = data.errors as ValidationError
          throw error
        }
        console.error('API error:', data?.message)
        throw new Error(data?.message || 'Failed to edit company')
      }

      return {
        success: true,
        message: (data && data.message) || 'Company updated successfully',
        data: data?.data,
      }
    } catch (err: any) {
      console.error('Error editing company:', err)

      // Handle axios error responses (including 422 validation errors)
      if (err.response) {
        const status = err.response.status
        const data = err.response.data

        // Handle validation errors from server (422 status)
        if (status === 422 && data?.errors) {
          const error: any = new Error(data?.message || 'Validation error')
          error.validationErrors = data.errors as ValidationError
          throw error
        }

        // Handle other API errors
        throw new Error(data?.message || err.message || 'Network error')
      }

      // Re-throw if already has validation errors attached
      if (err.validationErrors) {
        throw err
      }

      throw err
    }
  }

  const deleteCompany = async (companyId: number) => {
    try {
      const response = await apiInstance.delete(`/companies/${companyId}`)
      const data = response.data

      if (response.status < 200 || response.status >= 300 || (data && data.error === true)) {
        console.error('API error:', data?.message)
        throw new Error(data?.message || 'Failed to delete company')
      }

      return {
        success: true,
        message: (data && data.message) || 'Company deleted successfully',
      }
    } catch (err: any) {
      console.error('Error deleting company:', err)
      throw new Error(err.response?.data?.message || err.message || 'Network error')
    }
  }

  const reset = () => {
    companies.value = []
    isLoading.value = false
    error.value = ''
    successMessage.value = ''
  }

  return {
    companies,
    isLoading,
    error,
    successMessage,
    fetchCompanies,
    getCompanyById,
    createCompany,
    editCompany,
    deleteCompany,
    reset,
  }
})
